import { PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { ActionButton } from '@zeus/core/layout/MainLayout';
import UserProfile from '_/components/common/UserProfile';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useCollections } from '_/hooks/useCollections';
import useOrgAccess from '_/hooks/useOrgAccess';
import { ICollection } from '_/types/collections/collections.types';
import { formatWrittenDate } from '_/utils/dateUtils';
import { compareSort } from '_/utils/formatUtils';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Table, Input } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import React, { ReactElement, useCallback, useMemo, useState, useEffect } from 'react';

interface ExtendedCollection extends ICollection {
  collectionName: string;
  collectionId: string;
  href: string;
  createdOn: string;
  slug: string;
  linkSupported: boolean;
}

function ProjectCollections(): ReactElement {
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();
  const { data: rawCollections = [], refetch, isRefetching, isLoading } = useCollections(selectedProject?.id);
  const history = useNavigate();
  const [searchText, setSearchText] = useState('');
  const { setBreadcrumbs, setActionButtons, setOnRefresh, setRefreshLoading } = useTopHeader();

  // Transform collections to add href and formatted dates (like getCollectionsTable selector)
  const collections: ExtendedCollection[] = useMemo(
    () =>
      rawCollections.map((collection) => ({
        ...collection,
        collectionName: collection.name,
        collectionId: collection.id,
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${collection.slug}`,
        createdOn: formatWrittenDate(collection.addedAt),
        slug: collection.slug,
        linkSupported: true,
      })),
    [rawCollections, selectedOrgSlug, selectedProjectSlug],
  );

  // Filter collections based on search text
  const filteredCollections = collections.filter((collection: ExtendedCollection) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return collection.name?.toLowerCase().includes(searchLower) || collection.collectionName?.toLowerCase().includes(searchLower);
  });

  const handleNewCollection = useCallback(() => {
    void history({ to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/_new` });
  }, [history, selectedOrgSlug, selectedProjectSlug]);

  const handleRowClick = useCallback(
    (record: ICollection) => {
      void history({
        to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${record.slug}`,
      });
    },
    [history, selectedOrgSlug, selectedProjectSlug],
  );

  const actionButtons: ActionButton[] = useMemo(
    () => [
      {
        onClick: handleNewCollection,
        icon: <PlusCircleOutlined />,
        label: 'Add Collection',
        disabled: !access,
        tooltip: !access ? 'You do not have the right permissions to add a collection' : undefined,
      },
    ],
    [handleNewCollection, access],
  );

  // Set up header with breadcrumbs and action buttons
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      { title: 'Collections' },
    ]);
    setActionButtons(actionButtons);
    setOnRefresh(refetch);
    setRefreshLoading(isRefetching);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [
    setBreadcrumbs,
    setActionButtons,
    setOnRefresh,
    setRefreshLoading,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
    actionButtons,
    refetch,
    isRefetching,
  ]);

  const columns: ColumnType<ExtendedCollection>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: compareSort('name'),
      render: (name: string) => name,
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      sorter: compareSort('createdOn'),
    },
    {
      title: 'Assignee',
      dataIndex: 'assigneeId',
      key: 'assigneeId',
      sorter: compareSort('assigneeId'),
      render: (_: any, record: ExtendedCollection) => {
        const user = record.assignedUser || (record.assignee ? record.assignedUser : null);
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <UserProfile tableMode showDetails user={user} />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search collections..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 400 }}
          allowClear
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredCollections}
        rowKey="id"
        pagination={false}
        loading={isLoading}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}

export default ProjectCollections;
